import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getMesaByToken, getComandaByMesa } from '../services/api';
import { useCarrinho } from '../services/CarrinhoContext';
import Cardapio from './Cardapio';
import Loading from '../components/Loading';

const POLLING_INTERVAL = 8000; // 8 segundos

function TabletTokenDaMesa({ brand }) {
  const { token } = useParams();
  const { setMesa, setMesaToken, setTipoEntrega, setComandaExistente, setUltimaComanda, setComanda, resetMesaCompleto } = useCarrinho();
  const [mesaData, setMesaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);
  const lastComandaIdRef = useRef(null);
  const cleanTokenRef = useRef(null);

  useEffect(() => {
    if (brand && token) {
      cleanTokenRef.current = token.replace(/[^a-zA-Z0-9]/g, '');
      carregarMesa();
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [brand, token]);

  // Polling: verifica a cada 8s se a comanda ainda esta ativa
  // Quando PDV fecha a comanda, o tablet detecta e reseta tudo
  useEffect(() => {
    if (!mesaData || !brand || !cleanTokenRef.current) return;

    const verificarStatus = async () => {
      try {
        const resultado = await getComandaByMesa(brand, cleanTokenRef.current);

        if (!resultado || !resultado.comanda || 
            resultado.comanda.status === 'fechada' || 
            resultado.comanda.status === 'cancelada' ||
            resultado.comanda.status === 'entregue') {
          
          // Comanda foi fechada no PDV - resetar TUDO para proximo cliente
          if (lastComandaIdRef.current) {
            console.log('[Tablet] Comanda fechada pelo PDV, resetando para proximo cliente');
            lastComandaIdRef.current = null;
            resetMesaCompleto();
            
            // Re-setar a mesa (tablet continua na mesma mesa fisica)
            setMesa(mesaData);
            setMesaToken(cleanTokenRef.current);
            setTipoEntrega('local');
          }
        } else {
          // Comanda ativa - manter sincronizado
          const comanda = resultado.comanda;
          lastComandaIdRef.current = comanda.id;
          setComandaExistente(comanda);
          setUltimaComanda(comanda);
          setComanda({ id: comanda.id });
          localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
        }
      } catch (e) {
        // Erro de rede - nao fazer nada, tentar de novo no proximo ciclo
      }
    };

    // Executar imediatamente e depois a cada intervalo
    verificarStatus();
    pollingRef.current = setInterval(verificarStatus, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [mesaData, brand]);

  const carregarMesa = async () => {
    try {
      setLoading(true);
      const cleanToken = cleanTokenRef.current;
      
      const resultado = await getMesaByToken(brand, cleanToken);
      
      if (!resultado) {
        setError('Mesa nao encontrada');
        return;
      }

      setMesaData(resultado);
      setMesa(resultado);
      setMesaToken(cleanToken);
      setTipoEntrega('local');

      // Verificar se ja existe comanda aberta para esta mesa
      try {
        const comandaResult = await getComandaByMesa(brand, cleanToken);
        
        if (comandaResult && comandaResult.comanda && 
            comandaResult.comanda.status !== 'fechada' && 
            comandaResult.comanda.status !== 'cancelada' &&
            comandaResult.comanda.status !== 'entregue') {
          lastComandaIdRef.current = comandaResult.comanda.id;
          setComandaExistente(comandaResult.comanda);
          setUltimaComanda(comandaResult.comanda);
          setComanda({ id: comandaResult.comanda.id });
          localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comandaResult.comanda));
        } else {
          // Sem comanda ativa - estado limpo para novo cliente
          lastComandaIdRef.current = null;
          localStorage.removeItem('sitri_ultima_comanda');
          setComandaExistente(null);
          setUltimaComanda(null);
          setComanda(null);
        }
      } catch (e) {
        // Se falhar, continuar sem comanda
        lastComandaIdRef.current = null;
      }
      
    } catch (error) {
      console.error('[Tablet] Erro ao carregar mesa:', error);
      setError('Erro ao carregar dados da mesa');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message="Carregando mesa..." />;
  }

  if (error) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-danger">
          <h4>Erro</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!mesaData) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-warning">
          <h4>Mesa nao encontrada</h4>
        </div>
      </div>
    );
  }

  return <Cardapio brand={brand} mesaData={mesaData} />;
}

export default TabletTokenDaMesa;
